import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPartHistory } from '../services/orders';
import { checkDuplicate } from '../services/similarity';
import { getComparisonCache, saveComparisonCache } from '../services/analyses';
import DrawingPairModal, { AuthImage } from '../components/Offers/DrawingPairModal';
import './PartHistoryPage.css';

/** LAKPED-16.2 / LAKPED-21 — "qué sabemos ya" de la pieza, antes de montar la oferta.
 *
 *  Se lanzan a la vez dos búsquedas y se pintan por separado, cada una con su origen: lo del ERP
 *  (pedidos y ofertas con ese código de pieza) y los planos parecidos por comparación visual. Lo
 *  del ERP llega al segundo y se pinta de golpe; los parecidos tardan más y van con su espera.
 *  Cada resultado lleva su "Usar como base": solo se hereda uno, el que elija quien oferta.
 */
const PASOS = ['Documento', 'Histórico', 'Oferta', 'PDF y envío'];
const MAX_PARECIDOS = 8;

// En los datos que vienen de su Access, un importe o unas horas a cero significan "no consta",
// no "cero euros": por eso los dos casos se pintan igual, con una raya.
const fecha = (valor) => {
  if (!valor) return '—';
  const dia = new Date(valor);
  return Number.isNaN(dia.getTime()) ? '—' : dia.toLocaleDateString('es-ES');
};
const euros = (valor) => (valor ? valor.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) : '—');
const horas = (valor) => (valor ? `${valor.toFixed(2).replace('.', ',')} h` : '—');
const porcentaje = (valor) => `${Math.round((valor || 0) * 100)}%`;
const referenciaDe = (parecido) => parecido.result?._invoice_data?.articulo || null;
const clienteDe = (parecido) => parecido.result?._invoice_data?.cliente || null;

const PartHistoryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const analysisHash = searchParams.get('analysis') || '';
  const codigo = searchParams.get('codigo') || '';
  const cliente = searchParams.get('cliente') || '';
  const referencia = searchParams.get('referencia') || '';
  const cantidad = searchParams.get('cantidad') || '';
  // Una factura sin plano no tiene con qué compararse.
  const sinPlano = searchParams.get('plano') === '0';

  const [historico, setHistorico] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [parecidos, setParecidos] = useState(null);
  const [errorParecidos, setErrorParecidos] = useState(null);
  const [comparando, setComparando] = useState(null);
  const [preparandoBase, setPreparandoBase] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      setHistorico(await getPartHistory(codigo, analysisHash));
    } catch (err) {
      setError('No se ha podido consultar el histórico de la pieza.');
    } finally {
      setCargando(false);
    }
  }, [codigo, analysisHash]);

  useEffect(() => { cargar(); }, [cargar]);

  // La búsqueda visual reutiliza el documento ya guardado: no hay que volver a subirlo. Y el
  // resultado se guarda en la comparativa del documento (la misma caché que usa la búsqueda), para
  // que volver al histórico desde la oferta no cueste otros 40 segundos.
  useEffect(() => {
    if (!analysisHash || sinPlano) {
      setParecidos({ matches: [], topOverall: [], scores: {} });
      return;
    }
    let vivo = true;
    const pintar = (data) => setParecidos({
      matches: data.matches || [],
      topOverall: (data.top_overall || []).filter(t => !(data.matches || []).some(m => m.hash === (t.analysis_hash || t.hash))),
      scores: data.scores || {},
    });
    const buscar = () => checkDuplicate(null, { excludeHash: analysisHash, reuseFileFromHash: analysisHash })
      .then(data => {
        if (!vivo) return;
        pintar(data);
        saveComparisonCache(analysisHash, {
          matches: data.matches || [],
          scores: data.scores || {},
          top_overall: data.top_overall || [],
          searched_hashes: data.searched_hashes || [],
          stage_status: { stage1: 'completed', stage2: 'completed', stage3: 'completed' },
          stage_counts: {},
          heuristic_by_page: {},
          page_numbers: [],
        }).catch(() => {});
      });
    getComparisonCache(analysisHash)
      .then(res => {
        if (!vivo) return null;
        if (res?.cached && res.cached.matches !== undefined) { pintar(res.cached); return null; }
        return buscar();
      })
      .catch(() => buscar())
      .catch(() => { if (vivo) { setErrorParecidos('No se han podido buscar planos parecidos.'); setParecidos({ matches: [], topOverall: [], scores: {} }); } });
    return () => { vivo = false; };
  }, [analysisHash, sinPlano]);

  // Lo de la portada va siempre; lo de la base, solo lo que esa base sepa.
  const irALaOferta = (base = {}) => {
    const query = new URLSearchParams({ analysis: analysisHash });
    if (cliente) query.set('cliente', cliente);
    if (referencia) query.set('referencia', referencia);
    if (codigo || base.pieza) query.set('pieza', codigo || base.pieza);
    if (cantidad || base.cantidad) query.set('cantidad', cantidad || base.cantidad);
    if (base.descripcion) query.set('descripcion', base.descripcion);
    if (base.precio !== null && base.precio !== undefined) query.set('precio', base.precio);
    if (base.origen) query.set('origen', base.origen);
    navigate(`/offers/new?${query.toString()}`);
  };

  const usarPedido = (p) => irALaOferta({
    cantidad: p.cantidad,
    descripcion: p.descripcion_pieza,
    precio: p.precio_unitario,
    origen: [`pedido ${fecha(p.fecha)}`, p.cliente, p.referencia_cliente].filter(Boolean).join(' · '),
  });

  const usarOferta = (o) => irALaOferta({
    cantidad: o.cantidad,
    descripcion: o.descripcion_pieza,
    precio: o.precio_unitario,
    origen: [`oferta ${fecha(o.fecha)}`, o.cliente, o.estado].filter(Boolean).join(' · '),
  });

  const primeroConPrecio = (h) => [...(h?.pedidos || []), ...(h?.ofertas || [])].find(x => x.precio_unitario);

  // Un plano con el mismo código trae el último precio de esta pieza, que es el del histórico de arriba.
  const usarPlanoMismoCodigo = (p) => {
    const conPrecio = primeroConPrecio(historico);
    irALaOferta({
      cantidad: conPrecio?.cantidad,
      descripcion: conPrecio?.descripcion_pieza,
      precio: conPrecio?.precio_unitario,
      origen: `plano anterior ${p.filename} · mismo código${p.cliente ? ` · ${p.cliente}` : ''}`,
    });
  };

  // De un plano parecido se hereda el último precio de SU pieza, si la tiene y se ha pedido alguna vez.
  const usarParecido = async (s, score) => {
    const ref = referenciaDe(s);
    const origen = `plano parecido ${s.filename} (${porcentaje(score)})${ref ? ` · pieza ${ref}` : ''}`;
    setPreparandoBase(true);
    try {
      const conPrecio = primeroConPrecio(ref ? await getPartHistory(ref) : null);
      irALaOferta({
        pieza: ref,
        cantidad: conPrecio?.cantidad,
        descripcion: conPrecio?.descripcion_pieza,
        precio: conPrecio?.precio_unitario,
        origen,
      });
    } catch (err) {
      irALaOferta({ pieza: ref, origen });
    } finally {
      setPreparandoBase(false);
    }
  };

  const resumen = historico?.resumen || {};
  const pedidos = historico?.pedidos || [];
  const ofertas = historico?.ofertas || [];
  const planos = historico?.planos || [];
  const hayErp = pedidos.length > 0 || ofertas.length > 0;
  const listaParecidos = parecidos
    ? [
        ...parecidos.matches.map(m => ({ ...m, score: parecidos.scores[m.hash], grupo: 'Muy parecido' })),
        ...parecidos.topOverall.map(t => ({ ...t, hash: t.hash || t.analysis_hash, score: t.score ?? t.similarity_score, grupo: 'Cercano' })),
      ].slice(0, MAX_PARECIDOS)
    : [];

  // La misma tarjeta para los dos bloques de planos: cambia la etiqueta (porcentaje o "mismo código").
  const tarjetaPlano = ({ hash, filename, page, queryPage, etiqueta, grupo, verde, detalle, onBase }) => (
    <li key={hash} className="part-history-parecido">
      <AuthImage analysisHash={hash} page={page} alt={filename} className="part-history-miniatura" />
      <div className="part-history-parecido-datos">
        <b>{etiqueta} <span className={`part-history-chip ${verde ? 'verde' : ''}`}>{grupo}</span></b>
        <span className="part-history-parecido-nombre" title={filename}>{filename}</span>
        <span>{detalle}</span>
        <div className="part-history-parecido-acciones">
          <button type="button" className="part-history-secundario" onClick={() => setComparando({ hash, filename, page, queryPage })}>
            Ver en paralelo
          </button>
          <button type="button" className="part-history-principal" onClick={onBase} disabled={preparandoBase}>
            Usar como base
          </button>
        </div>
      </div>
    </li>
  );

  const tarjetaMismoCodigo = (p) => tarjetaPlano({
    hash: p.analysis_hash,
    filename: p.filename,
    page: null,
    etiqueta: codigo,
    grupo: 'Mismo código',
    verde: true,
    detalle: [p.cliente, fecha(p.fecha_documento || p.analyzed_at)].filter(x => x && x !== '—').join(' · ') || 'sin cliente',
    onBase: () => usarPlanoMismoCodigo(p),
  });

  const tarjetaParecido = (s) => tarjetaPlano({
    hash: s.hash,
    filename: s.filename,
    page: s.matched_page,
    etiqueta: porcentaje(s.score),
    grupo: s.grupo,
    verde: s.grupo === 'Muy parecido',
    detalle: [referenciaDe(s) ? `pieza ${referenciaDe(s)}` : 'sin código de pieza', clienteDe(s)].filter(Boolean).join(' · '),
    onBase: () => usarParecido(s, s.score),
    queryPage: s.query_page,
  });

  return (
    <div className="part-history">
      <ol className="part-history-pasos">
        {PASOS.map((titulo, i) => (
          <li key={titulo} className={i === 1 ? 'actual' : (i === 0 ? 'hecho' : '')}>
            {i === 0 ? (
              <button type="button" onClick={() => navigate(`/similarity-search?documento=${analysisHash}`)} disabled={!analysisHash}>
                <span className="part-history-paso-num">✓</span>
                {titulo}
              </button>
            ) : (
              <>
                <span className="part-history-paso-num">{i + 1}</span>
                {titulo}
              </>
            )}
          </li>
        ))}
      </ol>

      <div className="part-history-cabecera-pagina">
        <div>
          <h1>{cliente ? `Oferta para ${cliente} · qué sabemos ya` : 'Qué sabemos ya de esta pieza'}</h1>
          <p className="part-history-sub">
            Se busca a la vez por el código de la pieza en el ERP y por parecido visual del plano.
            {referencia ? ` Nº de pedido del cliente: ${referencia}.` : ''}{cantidad ? ` Cantidad: ${cantidad}.` : ''}
          </p>
        </div>
        <div className="part-history-acciones">
          <button
            type="button"
            className="part-history-secundario"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/similarity-search'))}
          >
            Atrás
          </button>
          <button type="button" className="part-history-principal" onClick={() => irALaOferta()}>
            Empezar de cero
          </button>
        </div>
      </div>

      {error && <p className="part-history-error">{error}</p>}

      <div className="part-history-pieza">
        <div className="part-history-cabecera">
          <div>
            <strong>Del ERP · {codigo || 'pieza sin código'}</strong>
            {historico?.usable === false && (
              <span className="part-history-aviso">
                {codigo
                  ? ' · este código es demasiado corto para buscar con él'
                  : ' · el documento no trae código de pieza, así que no hay por dónde buscar'}
              </span>
            )}
          </div>
          <div className="part-history-chips">
            {cargando ? (
              <span className="part-history-chip">Buscando…</span>
            ) : hayErp ? (
              <>
                <span className="part-history-chip verde">
                  Pedida {resumen.veces_pedida} {resumen.veces_pedida === 1 ? 'vez' : 'veces'}
                </span>
                {resumen.horas_apuntadas > 0 && (
                  <span className="part-history-chip">{horas(resumen.horas_apuntadas)} apuntadas</span>
                )}
              </>
            ) : (
              <span className="part-history-chip ambar">Sin histórico con este código</span>
            )}
          </div>
        </div>

        {!cargando && pedidos.length > 0 && (
          <div className="part-history-cuerpo">
            <h3>Pedidos de esta pieza</h3>
            <table className="part-history-tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th className="der">Uds</th>
                  <th className="der">Precio ud.</th>
                  <th className="der">Horas</th>
                  <th className="der">Importe</th>
                  <th>Pedido</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pedidos.slice(0, 10).map(p => (
                  <tr key={p.hash}>
                    <td>{fecha(p.fecha)}</td>
                    <td>{p.cliente || '—'}</td>
                    <td className="der">{p.cantidad ?? '—'}</td>
                    <td className="der"><b>{euros(p.precio_unitario)}</b></td>
                    <td className="der">{horas(p.horas)}</td>
                    <td className="der">{euros(p.importe)}</td>
                    <td>
                      <button type="button" onClick={() => navigate(`/orders/${p.hash}`)}>
                        {p.referencia_cliente || 'ver'}
                      </button>
                    </td>
                    <td className="der">
                      <button type="button" className="part-history-base" onClick={() => usarPedido(p)}>Usar como base</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {historico.total_pedidos > 10 && (
              <p className="part-history-pie">Se enseñan los 10 más recientes de {historico.total_pedidos}.</p>
            )}
          </div>
        )}

        {!cargando && ofertas.length > 0 && (
          <div className="part-history-cuerpo">
            <h3>Ofertas anteriores de esta pieza</h3>
            <table className="part-history-tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th className="der">Uds</th>
                  <th className="der">Precio ud.</th>
                  <th>Estado</th>
                  <th>Oferta</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {ofertas.slice(0, 5).map(o => (
                  <tr key={o.hash}>
                    <td>{fecha(o.fecha)}</td>
                    <td>{o.cliente || '—'}</td>
                    <td className="der">{o.cantidad ?? '—'}</td>
                    <td className="der"><b>{euros(o.precio_unitario)}</b></td>
                    <td>{o.estado}</td>
                    <td><button type="button" onClick={() => navigate(`/offers/${o.hash}`)}>ver</button></td>
                    <td className="der">
                      <button type="button" className="part-history-base" onClick={() => usarOferta(o)}>Usar como base</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!cargando && historico && !hayErp && (
          <div className="part-history-cuerpo">
            <p className="part-history-vacio">
              No hay ningún pedido ni oferta anterior con este código. Puede ser una pieza nueva,
              o que el cliente haya cambiado el código.
            </p>
          </div>
        )}
      </div>

      <div className="part-history-pieza part-history-bloque">
        <div className="part-history-cabecera">
          <div><strong>Planos con el mismo código de pieza</strong></div>
          <div className="part-history-chips">
            {cargando ? (
              <span className="part-history-chip">Buscando…</span>
            ) : (
              <span className={`part-history-chip ${planos.length ? 'verde' : 'ambar'}`}>
                {planos.length ? `${historico?.total_planos || planos.length} encontrados` : 'Ninguno'}
              </span>
            )}
          </div>
        </div>
        <div className="part-history-cuerpo">
          {!cargando && planos.length === 0 && (
            <p className="part-history-pie">Ningún plano analizado lleva este código en su documento.</p>
          )}
          {planos.length > 0 && <ul className="part-history-parecidos">{planos.slice(0, MAX_PARECIDOS).map(tarjetaMismoCodigo)}</ul>}
        </div>
      </div>

      <div className="part-history-pieza part-history-bloque">
        <div className="part-history-cabecera">
          <div><strong>Planos parecidos · comparación visual</strong></div>
          <div className="part-history-chips">
            {parecidos === null ? (
              <span className="part-history-chip"><i className="part-history-spinner" /> Comparando con los planos ya trabajados…</span>
            ) : (
              <span className={`part-history-chip ${listaParecidos.length ? 'verde' : 'ambar'}`}>
                {listaParecidos.length ? `${listaParecidos.length} encontrados` : 'Ninguno'}
              </span>
            )}
          </div>
        </div>
        <div className="part-history-cuerpo">
          {errorParecidos && <p className="part-history-error">{errorParecidos}</p>}
          {parecidos === null && (
            <p className="part-history-pie">Tarda más que lo del ERP. Se puede empezar la oferta sin esperar.</p>
          )}
          {parecidos !== null && listaParecidos.length === 0 && !errorParecidos && (
            <p className="part-history-pie">
              {sinPlano
                ? 'El documento no trae plano (solo factura o pedido), así que no hay con qué buscar parecidos.'
                : analysisHash
                  ? 'Ningún plano ya trabajado se parece a este.'
                  : 'No hay plano con el que comparar, así que los parecidos no se han podido mirar.'}
            </p>
          )}
          {listaParecidos.length > 0 && (
            <>
              <ul className="part-history-parecidos">{listaParecidos.map(tarjetaParecido)}</ul>
              <p className="part-history-pie">
                Esto es un parecido visual, no una coincidencia de código: míralo en paralelo antes de usarlo.
              </p>
            </>
          )}
        </div>
      </div>

      {comparando && (
        <DrawingPairModal
          izquierda={{ titulo: 'Plano subido', analysisHash, page: comparando.queryPage }}
          derecha={{ titulo: comparando.filename, analysisHash: comparando.hash, page: comparando.page }}
          onClose={() => setComparando(null)}
        />
      )}
    </div>
  );
};

export default PartHistoryPage;
